<template>
  <v-container fluid>
    <v-row justify-md="space-between" class="align-center mb-4">
      <v-col cols="12" md="auto">
        <v-btn @click="openFormDialog" class="accent shadow-4 px-8" color="success" large rounded>
          Añadir Plantilla Meta
        </v-btn>
      </v-col>

      <v-col cols="12" md="4" class="ml-auto">
        <v-text-field
          filled
          rounded
          dense
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar plantilla..."
          single-line
          hide-details
          clearable
        ></v-text-field>
      </v-col>
    </v-row>

    <v-card class="shadow-1">
      <v-data-table
        :headers="headers"
        :items="templatesList"
        item-key="id"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="totalItems"
        show-expand
        single-expand
        :footer-props="{
          'items-per-page-options': [15, 30, 50, 100],
        }"
      >
        <!-- Formato para Nombre / Identificador -->
        <template v-slot:[`item.name`]="{ item }">
          <span class="font-weight-medium text-caption grey--text text--dark-4">{{ item.name }}</span>
        </template>

        <!-- Formato para Tipo -->
        <template v-slot:[`item.type`]="{ item }">
          {{ item.type || "N/A" }}
        </template>

        <!-- Formato para Global -->
        <template v-slot:[`item.is_global`]="{ item }">
          {{ item.is_global ? "Sí" : "No" }}
        </template>

        <!-- Formato para Archivos -->
        <template v-slot:[`item.file`]="{ item }">
          <v-icon :color="item.file ? 'primary' : 'grey lighten-1'">
            {{ item.file ? "mdi-file-image" : "mdi-file-hidden" }}
          </v-icon>
        </template>

        <!-- Acciones -->
        <template v-slot:[`item.action`]="{ item }">
          <v-hover v-slot:default="{ hover }">
            <v-btn text icon :color="hover ? 'accent' : 'blue-grey'" @click.stop="handleEdit(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </v-hover>

          <v-hover v-slot:default="{ hover }">
            <v-btn text icon :color="hover ? 'error' : 'blue-grey'" @click.stop="handleDelete(item.id)">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-hover>
        </template>

        <!-- Fila Desplegable (Detalles del mensaje y multimedia) -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="py-4 grey lighten-4">
            <v-container fluid>
              <v-row>
                <!-- Vista previa del mensaje -->
                <v-col cols="12" md="7">
                  <div class="font-weight-bold grey--text text--dark-2 mb-1">Cuerpo del Mensaje:</div>
                  <v-card outlined class="pa-3 rounded-lg white font-weight-regular text-body-2">
                    {{ item.message || "Sin mensaje definido" }}
                  </v-card>
                </v-col>

                <!-- Vista previa de imagen / adjunto -->
                <v-col cols="12" md="5" v-if="item.file && item.image_url">
                  <div class="font-weight-bold grey--text text--dark-2 mb-1">Imagen Adjunta:</div>
                  <v-img
                    :src="item.image_url"
                    max-height="150"
                    contain
                    class="rounded-lg grey lighten-3 align-end"
                  ></v-img>
                </v-col>

                <!-- Parámetros dinámicos y metadata -->
                <v-col cols="12" class="mt-2">
                  <v-row dense class="text-caption">
                    <v-col cols="12" sm="4">
                      <span class="font-weight-bold">WhatsApp Config:</span> {{ getWhatsappName(item.whatsapp_id) }}
                    </v-col>
                    <v-col cols="12" sm="3">
                      <span class="font-weight-bold">N° Parámetros:</span> {{ item.parameter_number || 0 }}
                    </v-col>
                    <v-col cols="12" sm="5">
                      <span class="font-weight-bold">Parámetros clave:</span>
                      <v-chip
                        v-for="(param, idx) in item.parameter"
                        :key="idx"
                        small
                        class="ml-1"
                        color="blue lighten-5"
                        text-color="cyan darken-3"
                      >
                        {{ param }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <newForm
      v-if="isOpenForm"
      :isActive="isOpenForm"
      :dataToEdit="dataToEdit"
      :isEditMode="isEditMode"
      @close:dialog="closeFormDialog"
    ></newForm>
  </v-container>
</template>

<script>
import { PbxHttpClient } from "@/services/httpclient";
import newForm from "@/views/app/clientSettings/metaConfig/newForm.vue";

export default {
  components: {
    newForm,
  },

  data: () => ({
    isLoading: false,
    isLoadingConfigs: false,
    isOpenForm: false,
    isEditMode: false,
    totalItems: 0,
    search: "",
    whatsapp_id: null, // Puedes definir o pasar el ID dinámicamente si aplica (ej. 1)
    whatsappList: [],
    options: {
      page: 1,
      itemsPerPage: 15,
    },
    templatesList: [],
    dataToEdit: {},
    headers: [
      {
        text: "Título",
        align: "left",
        value: "title",
        class: "primary--text",
        width: "20%",
      },
      {
        text: "Identificador Meta",
        align: "left",
        value: "name",
        class: "primary--text",
        width: "20%",
      },
      {
        text: "Tipo",
        value: "type",
        class: "primary--text",
        width: "15%",
      },
      {
        text: "Global",
        value: "is_global",
        class: "primary--text",
        width: "10%",
        align: "center",
      },
      {
        text: "Adjunto",
        value: "file",
        class: "primary--text",
        width: "10%",
        align: "center",
      },
      {
        text: "Acciones",
        value: "action",
        class: "primary--text",
        width: "15%",
        align: "center",
        sortable: false,
      },
      {
        text: "",
        value: "data-table-expand",
        class: "primary--text",
        width: "5%",
      },
    ],
  }),

  watch: {
    options: {
      handler() {
        this.getTemplates();
      },
      deep: true,
    },
    search() {
      // Reiniciar a la primera página al realizar una nueva búsqueda
      this.options.page = 1;
      this.getTemplates();
    },
  },

  created() {
    this.getWhatsappConfigs();
    this.getTemplates();
  },

  methods: {
    openFormDialog() {
      this.isOpenForm = true;
    },

    closeFormDialog(payload) {
      this.isOpenForm = false;
      this.isEditMode = false;
      this.dataToEdit = {};

      if (payload) this.getTemplates();
    },

    handleEdit(item) {
      this.isEditMode = true;
      this.dataToEdit = { ...item };
      this.openFormDialog();
    },

    async handleDelete(id) {
      try {
        const confirmRequest = await this.$swalconfirm(
          "Confirmar",
          `¿Está seguro de eliminar esta plantilla? Esta acción no se puede deshacer.`,
        );
        if (confirmRequest.isConfirmed) {
          await PbxHttpClient.delete(`/templates-Meta/${id}`);

          const index = this.templatesList.findIndex((el) => el.id === id);
          if (index !== -1) this.templatesList.splice(index, 1);

          this.$snotify.success("Plantilla eliminada con éxito", "¡Hecho!");
        }
      } catch (error) {
        this.$snotify.error("No se pudo eliminar la plantilla.", "¡Error!");
      }
    },

    async getTemplates() {
      this.isLoading = true;
      const { page, itemsPerPage } = this.options;

      try {
        const params = {
          page,
          itemPerPage: itemsPerPage,
          search: this.search || undefined,
          whatsapp_id: this.whatsapp_id || undefined,
        };

        const { data } = await PbxHttpClient.get("/templates-Meta/", { params });

        this.templatesList = data.data || data || [];
        this.totalItems = data.total || this.templatesList.length;
      } catch (error) {
        this.$snotify.error("Hubo un error al obtener las plantillas.", "Error");
      } finally {
        this.isLoading = false;
      }
    },

    async getWhatsappConfigs() {
      this.isLoadingConfigs = true;
      try {
        const { data } = await PbxHttpClient.get("/whatsapps/config/index_select");
        this.whatsappList = data || [];
      } catch (err) {
        this.$snotify.error("No se pudo obtener el listado de WhatsApp.", "Error");
      } finally {
        this.isLoadingConfigs = false;
      }
    },

    getWhatsappName(id) {
      if (!id) return "N/A";
      const config = this.whatsappList.find((item) => item.id === id);
      return config ? config.name || config.title || config.phone_number || `ID: ${id}` : `ID: ${id}`;
    },
  },
};
</script>
